import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  MerchantOrderStatusEnum,
  PickingJobResolutionDestination,
  PickingJobSegment,
  PickingJobStatus,
} from '@/graphql/generatedTypes';
import { useResolvePickingJobMutation } from '@/graphql/mutations/resolvePickingJob.generated';
import i18n from '@/utils/i18n';
import {
  ERROR_MESSAGE_KEYS,
  MAX_REASON_LENGTH,
  destinationBlockedReasons,
  destinationDetails,
  isAcceptableReason,
  isKnownErrorCode,
  jobBlockedReason,
} from './pickingJobResolutionRules';

const { Warehouse: WAREHOUSE, Partner: PARTNER } =
  PickingJobResolutionDestination;
const DESTINATIONS = [WAREHOUSE, PARTNER];

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * A GraphQlError reaches the admin app with the code as its message and the
 * human-readable reason in extensions.exception.reason.
 */
function showActionError(error: unknown) {
  let code: string | undefined;
  let reason: string | undefined;
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    const first = error.graphQLErrors[0];
    const exception = first.extensions?.exception as
      | Record<string, unknown>
      | undefined;
    code = asString(exception?.error) ?? first.message;
    reason = asString(exception?.reason) ?? asString(first.extensions?.reason);
  }
  const key =
    code && isKnownErrorCode(code) ? ERROR_MESSAGE_KEYS[code] : undefined;
  if (key) {
    toast.error(i18n.t(key), { description: reason });
  } else {
    toast.error(reason ?? i18n.t('Common:error'));
  }
}

const DESTINATION_LABEL_KEYS: Record<PickingJobResolutionDestination, string> =
  {
    [WAREHOUSE]: 'Picking:actionResolveWarehouse',
    [PARTNER]: 'Picking:actionResolvePartner',
  };

const SUCCESS_KEYS: Record<PickingJobResolutionDestination, string> = {
  [WAREHOUSE]: 'Picking:actionResolveSuccessWarehouse',
  [PARTNER]: 'Picking:actionResolveSuccessPartner',
};

export function PickingJobActions({
  pickingJobId,
  onSettled,
  ...facts
}: {
  pickingJobId: string;
  segment: PickingJobSegment;
  status: PickingJobStatus;
  /**
   * The statuses of the orders the job owns (PickingJobOpsOrder.owned_by_job).
   * An order a newer live job holds is ignored by colugo's gates, so it must
   * not decide a blocked reason here either.
   */
  orderStatuses: MerchantOrderStatusEnum[];
  /** Every order linked to the job, owned or not. */
  linkedOrderCount: number;
  /**
   * PickingJob.has_booked_or_delivered_rows: colugo refuses to take an event
   * job with such rows back to the warehouse.
   */
  hasBookedOrDeliveredRows: boolean;
  /** PickingJob.resolution is set: colugo refuses a second resolution. */
  resolved: boolean;
  /** After a resolution, succeeded or refused: the job may have changed. */
  onSettled: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [destination, setDestination] =
    useState<PickingJobResolutionDestination | null>(null);
  const [reason, setReason] = useState('');
  const [resolve, { loading }] = useResolvePickingJobMutation();

  const jobBlocked = jobBlockedReason(facts);
  const blockedBy = destinationBlockedReasons(facts);
  const available = DESTINATIONS.filter((d) => blockedBy[d] == null);
  const buttonBlocked =
    jobBlocked ??
    (available.length === 0
      ? DESTINATIONS.map((d) => blockedBy[d]).join(' ')
      : null);

  // The refetch after a refusal can make the chosen destination unavailable
  // (someone else resolved the job meanwhile); the dialog then says why.
  const canConfirm =
    !loading &&
    jobBlocked == null &&
    destination != null &&
    blockedBy[destination] == null &&
    isAcceptableReason(reason);

  const openDialog = () => {
    setReason('');
    setDestination(available.length === 1 ? available[0] : null);
    setOpen(true);
  };

  const submit = async () => {
    if (!canConfirm) return;
    try {
      await resolve({
        variables: {
          picking_job_id: pickingJobId,
          destination,
          reason: reason.trim(),
        },
      });
      toast.success(i18n.t(SUCCESS_KEYS[destination]));
      setOpen(false);
    } catch (error) {
      // The dialog stays open with the reason kept, so a transient refusal
      // (a label retry running) can simply be confirmed again.
      showActionError(error);
    }
    onSettled();
  };

  const button = (
    <Button
      variant="outline"
      className="tw:w-full"
      disabled={loading || buttonBlocked != null}
      onClick={openDialog}
    >
      {i18n.t('Picking:actionResolve')}
    </Button>
  );

  return (
    <>
      {buttonBlocked == null ? (
        button
      ) : (
        <Tooltip>
          {/* A disabled button fires no pointer events; the wrapper is what
              shows the tooltip. */}
          <TooltipTrigger asChild>
            <span className="tw:inline-block tw:w-full">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{buttonBlocked}</TooltipContent>
        </Tooltip>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('Picking:actionResolveTitle')}</DialogTitle>
            <DialogDescription>
              {i18n.t('Picking:actionResolveDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {jobBlocked != null && (
              <p className="tw:mb-2 tw:text-sm tw:text-destructive">
                {jobBlocked}
              </p>
            )}
            <div
              role="radiogroup"
              className="tw:mb-4 tw:flex tw:flex-col tw:gap-2"
            >
              {DESTINATIONS.map((d) => (
                <DestinationOption
                  key={d}
                  label={i18n.t(DESTINATION_LABEL_KEYS[d])}
                  details={destinationDetails(d, facts.segment)}
                  blockedReason={blockedBy[d]}
                  selected={destination === d}
                  onSelect={() => setDestination(d)}
                />
              ))}
            </div>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={MAX_REASON_LENGTH}
              className="tw:min-h-24 tw:resize-none"
              placeholder={i18n.t('Picking:actionReasonPlaceholder')}
            />
            <div className="tw:mt-2 tw:flex tw:justify-between tw:gap-4 tw:text-sm tw:text-muted-foreground">
              <p>{i18n.t('Picking:actionReasonHint')}</p>
              <p className="tw:shrink-0 tw:tabular-nums">
                {i18n.t('Picking:actionReasonCounter', {
                  length: reason.length,
                  max: MAX_REASON_LENGTH,
                })}
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={loading}>
                {i18n.t('Common:cancel')}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={!canConfirm}
            >
              {i18n.t('Picking:actionConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DestinationOption({
  label,
  details,
  blockedReason,
  selected,
  onSelect,
}: {
  label: string;
  details: string[];
  blockedReason: string | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const blocked = blockedReason != null;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={blocked}
      onClick={onSelect}
      className={cn(
        'tw:flex tw:flex-col tw:gap-2 tw:rounded-md tw:border tw:p-3 tw:text-left tw:transition-colors',
        selected ? 'tw:border-primary tw:bg-accent' : 'tw:border-border',
        blocked ? 'tw:cursor-not-allowed tw:opacity-70' : 'tw:hover:bg-accent',
      )}
    >
      <span className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium">
        <span
          aria-hidden
          className={cn(
            'tw:size-3.5 tw:shrink-0 tw:rounded-full tw:border',
            selected ? 'tw:border-4 tw:border-primary' : 'tw:border-input',
          )}
        />
        {label}
      </span>
      <ul className="tw:list-disc tw:space-y-1 tw:pl-5 tw:text-sm tw:text-muted-foreground">
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
      {blocked && (
        <span className="tw:text-sm tw:text-destructive">{blockedReason}</span>
      )}
    </button>
  );
}
